import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Table,
  Button,
  InputNumber,
  AutoComplete,
  Select,
  Tag,
  message,
  Modal,
  Form,
  Row,
  Col,
  Spin,
  Input,
  Switch,
  Drawer,
  Space,
} from "antd";
import {
  EditOutlined,
  SettingOutlined,
  GlobalOutlined,
  TeamOutlined,
  UserOutlined,
  SyncOutlined,
  MailOutlined,
  CalendarOutlined,
  DollarOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  CloseOutlined,
  RightOutlined,
  DownOutlined,
  FileProtectOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  SettingsContainer,
  PageHeader,
  TabsContainer,
  ContentSection,
  SectionHeader,
  ConfigCard,
  ConfigRow,
  TableContainer,
} from "./Settings.style";
import { API_CONST } from "../../../const";
import { ComplianceDocViewer } from "../../../components/ComplianceDocViewer/ComplianceDocViewer";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const { Option } = Select;

const PRICING_REFERENCE_TYPE_LABELS = {
  currency: "Currency",
  country: "Country",
  city: "City",
  workMode: "Work Mode",
  geoTier: "Geo Tier",
  marketType: "Market Type",
};


const Settings = () => {
  const [activeTab, setActiveTab] = useState("Global");
  const [rolePricingSubTab, setRolePricingSubTab] = useState("RolePricing");
  const [globalForm] = Form.useForm();
  const [clientForm] = Form.useForm();
  const [partnerForm] = Form.useForm();
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loadingRate, setLoadingRate] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingGlobalSettings, setLoadingGlobalSettings] = useState(false);
  const [savingGlobalSettings, setSavingGlobalSettings] = useState(false);

  const [globalConfig, setGlobalConfig] = useState({
    clientMarkup: 0,
    partnerDeduction: 0,
    defaultCurrency: "INR",
  });

  const [workSettings, setWorkSettings] = useState({
    workingDays: 22,
    workingHours: 8,
    paidLeave: 2,
  });
  const [loadingWorkSettings, setLoadingWorkSettings] = useState(false);
  const [savingWorkSettings, setSavingWorkSettings] = useState(false);
  const [workSettingsModalVisible, setWorkSettingsModalVisible] = useState(false);
  const [workSettingsForm] = Form.useForm();

  const [automationSettings, setAutomationSettings] = useState({
    timesheetGenerateDay: 1,
    invoiceDueDays: 30,
    taxPercentage: 0,
  });
  const [loadingAutomationSettings, setLoadingAutomationSettings] = useState(false);
  const [savingAutomationSettings, setSavingAutomationSettings] = useState(false);
  const [automationForm] = Form.useForm();
  const [automationModalVisible, setAutomationModalVisible] = useState(false);

  const [offlinePartnerSettings, setOfflinePartnerSettings] = useState({
    offlinePartnerMarkup: 20,
    offlinePartnerDeduction: 10,
  });
  const [loadingOfflinePartnerSettings, setLoadingOfflinePartnerSettings] = useState(false);
  const [savingOfflinePartnerSettings, setSavingOfflinePartnerSettings] = useState(false);
  const [offlinePartnerModalVisible, setOfflinePartnerModalVisible] = useState(false);
  const [offlinePartnerForm] = Form.useForm();

  const fetchGlobalSettings = async () => {
    setLoadingGlobalSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_GLOBAL_SETTINGS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setGlobalConfig((prev) => ({
          ...prev,
          clientMarkup: data.customerMarkup || 0,
          partnerDeduction: data.partnerDeduction || 0,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch global settings:", error);
    } finally {
      setLoadingGlobalSettings(false);
    }
  };

  const saveGlobalSettings = async (values) => {
    setSavingGlobalSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.UPDATE_GLOBAL_SETTINGS, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customerMarkup: values.clientMarkup,
          partnerDeduction: values.partnerDeduction,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setGlobalConfig((prev) => ({
          ...prev,
          clientMarkup: data.customerMarkup || values.clientMarkup,
          partnerDeduction: data.partnerDeduction || values.partnerDeduction,
        }));
        message.success("Global settings saved successfully");
        return true;
      } else {
        message.error(data.message || "Failed to save global settings");
        return false;
      }
    } catch (error) {
      console.error("Failed to save global settings:", error);
      message.error("Failed to save global settings");
      return false;
    } finally {
      setSavingGlobalSettings(false);
    }
  };

  const fetchWorkSettings = async () => {
    setLoadingWorkSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_WORK_SETTINGS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setWorkSettings({
          workingDays: data.workingDaysPerMonth || 22,
          workingHours: data.workingHoursPerDay || 8,
          paidLeave: data.paidLeavesPerMonth || 2,
        });
      }
    } catch (error) {
      console.error("Failed to fetch work settings:", error);
    } finally {
      setLoadingWorkSettings(false);
    }
  };

  const saveWorkSettings = async (values) => {
    setSavingWorkSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.UPDATE_WORK_SETTINGS, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          workingDaysPerMonth: values.workingDays,
          workingHoursPerDay: values.workingHours,
          paidLeavesPerMonth: values.paidLeave,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setWorkSettings({
          workingDays: values.workingDays,
          workingHours: values.workingHours,
          paidLeave: values.paidLeave,
        });
        message.success("Work settings saved successfully");
        return true;
      } else {
        message.error(data.message || "Failed to save work settings");
        return false;
      }
    } catch (error) {
      console.error("Failed to save work settings:", error);
      message.error("Failed to save work settings");
      return false;
    } finally {
      setSavingWorkSettings(false);
    }
  };

  const [emailSettings, setEmailSettings] = useState({
    smtpHost: "smtp.gmail.com",
    smtpPort: 587,
    senderEmail: "",
    senderName: "HireNXT Notifications",
    talentTeamEmail: "",
    interviewSchedulingEnabled: true,
    hiringAlertEnabled: true,
    jobAlertEnabled: true,
  });
  const [loadingEmailSettings, setLoadingEmailSettings] = useState(false);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);

  const [emailForm] = Form.useForm();
  const [emailSettingsModalVisible, setEmailSettingsModalVisible] = useState(false);

  const [clientConfigs, setClientConfigs] = useState([
    {
      id: 1,
      clientId: "CL001",
      clientName: "Tech Innovations Inc",
      location: "New York, USA",
      markup: 15,
      workingDays: 22,
      workingHours: 8,
      currency: "INR",
      paidLeave: 1,
    },
    {
      id: 2,
      clientId: "CL002",
      clientName: "Digital Solutions Ltd",
      location: "London, UK",
      markup: 15,
      workingDays: 22,
      workingHours: 8,
      currency: "INR",
      paidLeave: 1,
    },
    {
      id: 3,
      clientId: "CL003",
      clientName: "Global Tech Corp",
      location: "Bangalore, India",
      markup: 15,
      workingDays: 22,
      workingHours: 8,
      currency: "INR",
      paidLeave: 1,
    },
  ]);

  const [partnerConfigs, setPartnerConfigs] = useState([
    {
      id: 1,
      partnerId: "PT001",
      partnerName: "TechCorp Solutions",
      location: "Bangalore, India",
      deduction: 10,
      workingDays: 22,
      workingHours: 8,
      currency: "INR",
    },
    {
      id: 2,
      partnerId: "PT002",
      partnerName: "Digital Partners Inc",
      location: "Pune, India",
      deduction: 10,
      workingDays: 22,
      workingHours: 8,
      currency: "INR",
    },
    {
      id: 3,
      partnerId: "PT003",
      partnerName: "Innovate Tech",
      location: "Chennai, India",
      deduction: 10,
      workingDays: 22,
      workingHours: 8,
      currency: "INR",
    },
  ]);

  const [globalConfigModalVisible, setGlobalConfigModalVisible] = useState(false);
  const [clientEditModalVisible, setClientEditModalVisible] = useState(false);
  const [partnerEditModalVisible, setPartnerEditModalVisible] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const [rolePricingRecords, setRolePricingRecords] = useState([]);
  const [rolePricingTotal, setRolePricingTotal] = useState(0);
  const [rolePricingPage, setRolePricingPage] = useState(1);
  const [rolePricingPageSize, setRolePricingPageSize] = useState(20);
  const [rolePricingSearch, setRolePricingSearch] = useState("");
  const [rolePricingWorkMode, setRolePricingWorkMode] = useState("");
  const [rolePricingCity, setRolePricingCity] = useState("");
  const [rolePricingCountry, setRolePricingCountry] = useState("");
  const [rolePricingCategory, setRolePricingCategory] = useState("");
  const [rolePricingGeoTier, setRolePricingGeoTier] = useState("");
  const [rolePricingMarketType, setRolePricingMarketType] = useState("");
  const [rolePricingSource, setRolePricingSource] = useState("");
  const [rolePricingConfidence, setRolePricingConfidence] = useState("");
  const [loadingRolePricing, setLoadingRolePricing] = useState(false);
  const [addRoleModalVisible, setAddRoleModalVisible] = useState(false);
  const [editRoleRecord, setEditRoleRecord] = useState(null);
  const [savingRole, setSavingRole] = useState(false);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [rolePricingForm] = Form.useForm();
  const [pricingReferenceValues, setPricingReferenceValues] = useState({});
  const [loadingPricingReferences, setLoadingPricingReferences] = useState(false);
  const [referenceModalVisible, setReferenceModalVisible] = useState(false);
  const [editingReference, setEditingReference] = useState(null);
  const [savingReference, setSavingReference] = useState(false);
  const [referenceSearch, setReferenceSearch] = useState("");
  const [referenceTypeFilter, setReferenceTypeFilter] = useState("");
  const [referencePage, setReferencePage] = useState(1);
  const [referencePageSize, setReferencePageSize] = useState(20);
  const [referenceForm] = Form.useForm();

  const [otherSettingsSubTab, setOtherSettingsSubTab] = useState("CountriesCities");
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [addCityModalVisible, setAddCityModalVisible] = useState(false);
  const [newCityName, setNewCityName] = useState("");
  const [savingCity, setSavingCity] = useState(false);
  const [addCountryModalVisible, setAddCountryModalVisible] = useState(false);
  const [newCountryName, setNewCountryName] = useState("");
  const [savingCountry, setSavingCountry] = useState(false);
  const [editCountryModalVisible, setEditCountryModalVisible] = useState(false);
  const [editingCountryRecord, setEditingCountryRecord] = useState(null);
  const [editCountryName, setEditCountryName] = useState("");
  const [savingEditCountry, setSavingEditCountry] = useState(false);
  const [editCityModalVisible, setEditCityModalVisible] = useState(false);
  const [editingCityRecord, setEditingCityRecord] = useState(null);
  const [editCityName, setEditCityName] = useState("");
  const [savingEditCity, setSavingEditCity] = useState(false);
  const [expandedCountries, setExpandedCountries] = useState(new Set(["India"]));
  const [seedingReferenceValues, setSeedingReferenceValues] = useState(false);
  const [seedingRoles, setSeedingRoles] = useState(false);
  const [locationsData, setLocationsData] = useState({ countries: [], allCities: [] });

  const toggleCountry = (key) => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const getReferenceOptions = (type) => {
    return pricingReferenceValues[type] || [];
  };

  const getReferenceDefault = (type, fallback) => {
    const options = getReferenceOptions(type);
    return options.some((option) => option.value === fallback)
      ? fallback
      : options[0]?.value || fallback;
  };

  const renderReferenceOptions = (type) => getReferenceOptions(type).map((option) => (
    <Option key={`${type}-${option.value}`} value={option.value}>
      {option.label || option.value}
    </Option>
  ));

  const getReferenceAutoCompleteOptions = (type) => getReferenceOptions(type).map((option) => ({
    value: option.value,
    label: option.label || option.value,
  }));

  const pricingReferenceRows = Object.keys(PRICING_REFERENCE_TYPE_LABELS).flatMap((type) =>
    getReferenceOptions(type).map((item) => ({ ...item, type }))
  );

  const filteredPricingReferenceRows = pricingReferenceRows.filter((row) => {
    const normalizedSearch = referenceSearch.trim().toLowerCase();
    const matchesSearch = !normalizedSearch || [
      PRICING_REFERENCE_TYPE_LABELS[row.type] || row.type,
      row.label,
      row.value,
      row.sortOrder,
    ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
    const matchesType = !referenceTypeFilter || row.type === referenceTypeFilter;
    return matchesSearch && matchesType;
  });

  const fetchExchangeRate = async () => {
    setLoadingRate(true);
    try {
      const response = await fetch('https://api.frankfurter.dev/v1/latest?base=USD&symbols=INR');
      const data = await response.json();
      const rate = Math.round(data.rates.INR);
      setExchangeRate(rate);
      setLastUpdated(new Date().toLocaleString());
      message.success(`Exchange rate updated: 1 USD = ₹${rate}`);
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      message.error('Failed to fetch exchange rate. Please try again.');
    } finally {
      setLoadingRate(false);
    }
  };

  const fetchEmailSettings = async () => {
    setLoadingEmailSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_EMAIL_SETTINGS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok && data) {
        setEmailSettings((prev) => ({
          ...prev,
          talentTeamEmail: data.talentTeamEmail || "",
          senderEmail: data.senderEmail || "",
          senderName: data.senderName || "HireNXT Notifications",
          smtpHost: data.smtpHost || "smtp.gmail.com",
          smtpPort: data.smtpPort || 587,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch email settings:", error);
    } finally {
      setLoadingEmailSettings(false);
    }
  };

  const saveEmailSettings = async (values) => {
    setSavingEmailSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.UPDATE_EMAIL_SETTINGS, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (response.ok) {
        message.success("Email settings saved successfully");
        return true;
      } else {
        message.error(data.message || "Failed to save email settings");
        return false;
      }
    } catch (error) {
      console.error("Failed to save email settings:", error);
      message.error("Failed to save email settings");
      return false;
    } finally {
      setSavingEmailSettings(false);
    }
  };

  const fetchAutomationSettings = async () => {
    setLoadingAutomationSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_AUTOMATION_SETTINGS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setAutomationSettings({
          timesheetGenerateDay: data.timesheetGenerateDay || 1,
          invoiceDueDays: data.invoiceDueDays || 30,
          taxPercentage: data.taxPercentage || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch automation settings:", error);
    } finally {
      setLoadingAutomationSettings(false);
    }
  };

  const saveAutomationSettings = async (values) => {
    setSavingAutomationSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.UPDATE_AUTOMATION_SETTINGS, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (response.ok) {
        setAutomationSettings(values);
        message.success("Automation settings saved successfully");
        return true;
      } else {
        message.error(data.message || "Failed to save automation settings");
        return false;
      }
    } catch (error) {
      console.error("Failed to save automation settings:", error);
      message.error("Failed to save automation settings");
      return false;
    } finally {
      setSavingAutomationSettings(false);
    }
  };

  const fetchOfflinePartnerSettings = async () => {
    setLoadingOfflinePartnerSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.GET_OFFLINE_PARTNER_SETTINGS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setOfflinePartnerSettings({
          offlinePartnerMarkup: data.offlinePartnerMarkup ?? 20,
          offlinePartnerDeduction: data.offlinePartnerDeduction ?? 10,
        });
      }
    } catch (error) {
      console.error("Failed to fetch offline partner settings:", error);
    } finally {
      setLoadingOfflinePartnerSettings(false);
    }
  };

  const saveOfflinePartnerSettings = async (values) => {
    setSavingOfflinePartnerSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.UPDATE_OFFLINE_PARTNER_SETTINGS, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (response.ok) {
        setOfflinePartnerSettings({
          offlinePartnerMarkup: data.offlinePartnerMarkup ?? values.offlinePartnerMarkup,
          offlinePartnerDeduction: data.offlinePartnerDeduction ?? values.offlinePartnerDeduction,
        });
        message.success("Offline partner settings saved successfully");
        return true;
      } else {
        message.error(data.message || "Failed to save offline partner settings");
        return false;
      }
    } catch (error) {
      console.error("Failed to save offline partner settings:", error);
      message.error("Failed to save offline partner settings");
      return false;
    } finally {
      setSavingOfflinePartnerSettings(false);
    }
  };

  const fetchRolePricing = async (page = 1, overrides = {}) => {
    const filters = {
      search: rolePricingSearch,
      workMode: rolePricingWorkMode,
      city: rolePricingCity,
      country: rolePricingCountry,
      category: rolePricingCategory,
      geoTier: rolePricingGeoTier,
      marketType: rolePricingMarketType,
      source: rolePricingSource,
      confidence: rolePricingConfidence,
      ...overrides,
    };
    setLoadingRolePricing(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams({ page, limit: filters.limit || rolePricingPageSize });
      if (filters.search) params.set("search", filters.search);
      if (filters.workMode) params.set("workMode", filters.workMode);
      if (filters.city) params.set("city", filters.city);
      if (filters.country) params.set("country", filters.country);
      if (filters.category) params.set("category", filters.category);
      if (filters.geoTier) params.set("geoTier", filters.geoTier);
      if (filters.marketType) params.set("marketType", filters.marketType);
      if (filters.source) params.set("source", filters.source);
      if (filters.confidence) params.set("confidence", filters.confidence);
      const response = await fetch(`${API_CONST.ROLE_PRICING}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setRolePricingRecords(data.records || []);
        setRolePricingTotal(data.total || 0);
        setRolePricingPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch role pricing:", error);
    } finally {
      setLoadingRolePricing(false);
    }
  };

  const fetchPricingReferenceValues = async () => {
    setLoadingPricingReferences(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.ROLE_PRICING_REFERENCE_VALUES, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setPricingReferenceValues(data.values || {});
      } else {
        message.error(data.message || "Failed to load pricing reference values");
      }
    } catch (error) {
      console.error("Failed to fetch pricing reference values:", error);
      message.error("Failed to load pricing reference values");
    } finally {
      setLoadingPricingReferences(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const [cRes, ciRes] = await Promise.all([
        fetch(API_CONST.LOCATIONS_COUNTRIES, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(API_CONST.LOCATIONS_CITIES, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const cData = cRes.ok ? await cRes.json() : { countries: [] };
      const ciData = ciRes.ok ? await ciRes.json() : { cities: [] };
      setLocationsData({ countries: cData.countries || [], allCities: ciData.cities || [] });
    } catch {
    }
  };

  const handleOpenReferenceModal = (record = null) => {
    setEditingReference(record?.id ? record : null);
    referenceForm.resetFields();
    referenceForm.setFieldsValue(record?.id ? record : { type: "currency", sortOrder: 0 });
    setReferenceModalVisible(true);
  };

  const handleSaveReferenceValue = async () => {
    try {
      const values = await referenceForm.validateFields();
      setSavingReference(true);
      const token = localStorage.getItem("authToken");
      const url = editingReference ? `${API_CONST.ROLE_PRICING_REFERENCE_VALUES}/${editingReference.id}` : API_CONST.ROLE_PRICING_REFERENCE_VALUES;
      const method = editingReference ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(editingReference ? "Reference value updated" : "Reference value added");
        setReferenceModalVisible(false);
        setEditingReference(null);
        referenceForm.resetFields();
        fetchPricingReferenceValues();
      } else {
        message.error(data.message || "Failed to save reference value");
      }
    } catch (error) {
      console.error("Save pricing reference value error:", error);
    } finally {
      setSavingReference(false);
    }
  };

  const handleSeedReferenceValues = async () => {
    setSeedingReferenceValues(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.SEED_REFERENCE_VALUES, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        message.success(data.message || "Default values seeded successfully!");
        fetchPricingReferenceValues();
      } else {
        message.error(data.message || "Failed to seed reference values");
      }
    } catch (error) {
      console.error("Seed reference values error:", error);
      message.error("Failed to seed reference values");
    } finally {
      setSeedingReferenceValues(false);
    }
  };

  const handleSeedRoles = async () => {
    setSeedingRoles(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.ROLE_PRICING_SEED_ROLES, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        message.success(data.message || "Roles seeded successfully!");
        fetchRolePricing();
      } else {
        message.error(data.message || "Failed to seed roles");
      }
    } catch (error) {
      console.error("Seed roles error:", error);
      message.error("Failed to seed roles");
    } finally {
      setSeedingRoles(false);
    }
  };

  const handleDeleteReferenceValue = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const idToDelete = record?.id;
      if (!idToDelete) {
        message.error("Cannot delete: record has no ID");
        return;
      }

      const isLocationRecord = record.type === "country" || record.type === "city";
      const deleteUrl = isLocationRecord
        ? `${record.type === "country" ? API_CONST.LOCATIONS_COUNTRY : API_CONST.LOCATIONS_CITY}/${idToDelete}`
        : `${API_CONST.ROLE_PRICING_REFERENCE_VALUES}/${idToDelete}`;

      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        message.success("Deleted successfully");
        if (isLocationRecord) fetchLocations();
        fetchPricingReferenceValues();
      } else {
        message.error(data.message || "Failed to delete");
      }
    } catch (error) {
      console.error("Delete pricing reference value error:", error);
      message.error("Failed to delete");
    }
  };

  const handleAddCity = async () => {
    if (!newCityName.trim()) {
      message.error("Please enter a city name");
      return;
    }
    setSavingCity(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.LOCATIONS_CITY, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ value: newCityName.trim(), label: newCityName.trim(), country: selectedCountry, sortOrder: 100 }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(`${newCityName.trim()} added successfully`);
        setAddCityModalVisible(false);
        setNewCityName("");
        fetchLocations();
        fetchPricingReferenceValues();
      } else {
        message.error(data.message || "Failed to add city");
      }
    } catch {
      message.error("Failed to add city");
    } finally {
      setSavingCity(false);
    }
  };

  const handleAddCountry = async () => {
    if (!newCountryName.trim()) {
      message.error("Please enter a country name");
      return;
    }
    setSavingCountry(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.LOCATIONS_COUNTRY, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ value: newCountryName.trim(), label: newCountryName.trim(), sortOrder: 100 }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(`${newCountryName.trim()} added successfully`);
        setAddCountryModalVisible(false);
        setNewCountryName("");
        fetchLocations();
        fetchPricingReferenceValues();
      } else {
        message.error(data.message || "Failed to add country");
      }
    } catch {
      message.error("Failed to add country");
    } finally {
      setSavingCountry(false);
    }
  };

  const handleEditCountry = async () => {
    if (!editCountryName.trim()) {
      message.error("Please enter a country name");
      return;
    }
    setSavingEditCountry(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_CONST.LOCATIONS_COUNTRY}/${editingCountryRecord.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ value: editCountryName.trim(), label: editCountryName.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success("Country updated successfully");
        setEditCountryModalVisible(false);
        setEditingCountryRecord(null);
        setEditCountryName("");
        fetchLocations();
        fetchPricingReferenceValues();
      } else {
        message.error(data.message || "Failed to update country");
      }
    } catch {
      message.error("Failed to update country");
    } finally {
      setSavingEditCountry(false);
    }
  };

  const handleEditCity = async () => {
    if (!editCityName.trim()) {
      message.error("Please enter a city name");
      return;
    }
    setSavingEditCity(true);
    try {
      const token = localStorage.getItem("authToken");
      const isNew = !editingCityRecord.id;
      const url = isNew
        ? API_CONST.LOCATIONS_CITY
        : `${API_CONST.LOCATIONS_CITY}/${editingCityRecord.id}`;
      const response = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ value: editCityName.trim(), label: editCityName.trim(), sortOrder: editingCityRecord.sortOrder || 100 }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success("City updated successfully");
        setEditCityModalVisible(false);
        setEditingCityRecord(null);
        setEditCityName("");
        fetchLocations();
        fetchPricingReferenceValues();
      } else {
        message.error(data.message || "Failed to update city");
      }
    } catch {
      message.error("Failed to update city");
    } finally {
      setSavingEditCity(false);
    }
  };

  const handleSaveRoleRecord = async () => {
    try {
      const values = await rolePricingForm.validateFields();
      setSavingRole(true);
      const token = localStorage.getItem("authToken");
      const url = editRoleRecord ? `${API_CONST.ROLE_PRICING}/${editRoleRecord.id}` : API_CONST.ROLE_PRICING;
      const method = editRoleRecord ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(editRoleRecord ? "Record updated" : "Record created");
        setAddRoleModalVisible(false);
        setEditRoleRecord(null);
        rolePricingForm.resetFields();
        fetchRolePricing(rolePricingPage);
      } else {
        message.error(data.message || "Failed to save record");
      }
    } catch (err) {
      console.error("Save role record error:", err);
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRoleRecord = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_CONST.ROLE_PRICING}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        message.success("Record deleted");
        fetchRolePricing(rolePricingPage);
      } else {
        message.error("Failed to delete record");
      }
    } catch (err) {
      message.error("Failed to delete record");
    }
  };

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem("authToken");
    const a = document.createElement("a");
    a.href = API_CONST.ROLE_PRICING_TEMPLATE;
    fetch(API_CONST.ROLE_PRICING_TEMPLATE, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.download = "role_pricing_template.xlsx";
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBulkUploadLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(API_CONST.ROLE_PRICING_BULK_UPLOAD, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        message.success(`Bulk upload: ${data.succeeded}/${data.total} records created${data.failed > 0 ? `, ${data.failed} failed` : ""}`);
        if (data.errors?.length) {
          data.errors.slice(0, 3).forEach((e) => message.warning(`Row ${e.row}: ${e.error}`, 5));
        }
        fetchRolePricing(rolePricingPage);
      } else {
        message.error(data.message || "Bulk upload failed");
      }
    } catch (err) {
      message.error("Bulk upload failed");
    } finally {
      setBulkUploadLoading(false);
      event.target.value = "";
    }
  };

  useEffect(() => {
    fetchExchangeRate();
    fetchGlobalSettings();
    fetchWorkSettings();
    fetchEmailSettings();
    fetchAutomationSettings();
    fetchOfflinePartnerSettings();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (activeTab === "RolesPricing") {
      fetchPricingReferenceValues();
      fetchRolePricing(1);
    }
    if (activeTab === "OtherSettings") {
      fetchPricingReferenceValues();
    }
  }, [activeTab]);

  const handleUpdateAutomationSettings = async () => {
    try {
      const values = await automationForm.validateFields();
      const success = await saveAutomationSettings(values);
      if (success) {
        setAutomationModalVisible(false);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleUpdateOfflinePartnerSettings = async () => {
    try {
      const values = await offlinePartnerForm.validateFields();
      const success = await saveOfflinePartnerSettings(values);
      if (success) {
        setOfflinePartnerModalVisible(false);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleUpdateWorkSettings = async () => {
    try {
      const values = await workSettingsForm.validateFields();
      const success = await saveWorkSettings(values);
      if (success) {
        setWorkSettingsModalVisible(false);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleUpdateGlobalConfig = async () => {
    try {
      const values = await globalForm.validateFields();
      const success = await saveGlobalSettings(values);
      if (success) {
        setGlobalConfig((prev) => ({ ...prev, ...values }));
        setGlobalConfigModalVisible(false);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleEditClient = (record) => {
    setSelectedClient(record);
    clientForm.setFieldsValue({
      markup: record.markup,
      workingDays: record.workingDays,
      workingHours: record.workingHours,
      currency: record.currency,
      paidLeave: record.paidLeave,
    });
    setClientEditModalVisible(true);
  };

  const handleUpdateClient = () => {
    clientForm.validateFields().then((values) => {
      const updated = clientConfigs.map((c) =>
        c.id === selectedClient.id
          ? { ...c, ...values }
          : c
      );
      setClientConfigs(updated);
      setClientEditModalVisible(false);
      setSelectedClient(null);
      clientForm.resetFields();
      message.success(`Configuration updated for ${selectedClient.clientName}`);
    });
  };

  const handleEditPartner = (record) => {
    setSelectedPartner(record);
    partnerForm.setFieldsValue({
      deduction: record.deduction,
      workingDays: record.workingDays,
      workingHours: record.workingHours,
      currency: record.currency,
    });
    setPartnerEditModalVisible(true);
  };

  const handleUpdatePartner = () => {
    partnerForm.validateFields().then((values) => {
      const updated = partnerConfigs.map((p) =>
        p.id === selectedPartner.id
          ? { ...p, ...values }
          : p
      );
      setPartnerConfigs(updated);
      setPartnerEditModalVisible(false);
      setSelectedPartner(null);
      partnerForm.resetFields();
      message.success(`Configuration updated for ${selectedPartner.partnerName}`);
    });
  };

  const handleUpdateEmailSettings = async () => {
    try {
      const values = await emailForm.validateFields();
      const success = await saveEmailSettings(values);
      if (success) {
        setEmailSettings(values);
        setEmailSettingsModalVisible(false);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const clientColumns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      width: 180,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "Markup (%)",
      dataIndex: "markup",
      key: "markup",
      width: 100,
      render: (val) => (
        <Tag color="green" style={{ fontSize: 14, padding: "4px 12px" }}>
          {val}%
        </Tag>
      ),
    },
    {
      title: "Working Days",
      dataIndex: "workingDays",
      key: "workingDays",
      width: 120,
    },
    {
      title: "Working Hours",
      dataIndex: "workingHours",
      key: "workingHours",
      width: 120,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 100,
    },
    {
      title: "Paid Leave",
      dataIndex: "paidLeave",
      key: "paidLeave",
      width: 100,
      render: (val) => (
        <Tag color="blue" style={{ fontSize: 14, padding: "4px 12px" }}>
          {val}/month
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditClient(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const partnerColumns = [
    {
      title: "Partner Name",
      dataIndex: "partnerName",
      key: "partnerName",
      width: 180,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "Deduction (%)",
      dataIndex: "deduction",
      key: "deduction",
      width: 120,
      render: (val) => (
        <Tag color="orange" style={{ fontSize: 14, padding: "4px 12px" }}>
          {val}%
        </Tag>
      ),
    },
    {
      title: "Working Days",
      dataIndex: "workingDays",
      key: "workingDays",
      width: 120,
    },
    {
      title: "Working Hours",
      dataIndex: "workingHours",
      key: "workingHours",
      width: 120,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 100,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPartner(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <SettingsContainer>
      <PageHeader>
        <h1>Settings</h1>
        <p>Configure system-wide settings and pricing rules</p>
      </PageHeader>

      <TabsContainer>
        <Button
          className={`tab-button ${activeTab === "Global" ? "active" : ""}`}
          onClick={() => setActiveTab("Global")}
        >
          <GlobalOutlined /> Global Settings
        </Button>
        <Button
          className={`tab-button ${activeTab === "Client" ? "active" : ""}`}
          onClick={() => setActiveTab("Client")}
        >
          <UserOutlined /> Client Specific Configurations
        </Button>
        <Button
          className={`tab-button ${activeTab === "Partner" ? "active" : ""}`}
          onClick={() => setActiveTab("Partner")}
        >
          <TeamOutlined /> Partner Specific Configuration
        </Button>
        <Button
          className={`tab-button ${activeTab === "RolesPricing" ? "active" : ""}`}
          onClick={() => setActiveTab("RolesPricing")}
        >
          <DollarOutlined /> Roles &amp; Pricing
        </Button>
        <Button
          className={`tab-button ${activeTab === "OtherSettings" ? "active" : ""}`}
          onClick={() => setActiveTab("OtherSettings")}
        >
          <EnvironmentOutlined /> Other Settings
        </Button>
        <Button
          className={`tab-button ${activeTab === "ComplianceTemplates" ? "active" : ""}`}
          onClick={() => setActiveTab("ComplianceTemplates")}
        >
          <FileProtectOutlined /> Compliance Templates
        </Button>
      </TabsContainer>

      {activeTab === "Global" && (
        <div>
          <ContentSection style={{ marginBottom: 24 }}>
            <SectionHeader>
              <h3>Client Markup / Partner Deductions</h3>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  globalForm.setFieldsValue(globalConfig);
                  setGlobalConfigModalVisible(true);
                }}
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
              >
                Update Global Settings
              </Button>
            </SectionHeader>

            <ConfigCard>
              <ConfigRow>
                <span className="label">Client Markup</span>
                <span className="value">
                  <Tag color="green" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {globalConfig.clientMarkup}%
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Applied to all clients without specific markup
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">Partner Deduction</span>
                <span className="value">
                  <Tag color="orange" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {globalConfig.partnerDeduction}%
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Applied to all partners without specific deduction
                  </span>
                </span>
              </ConfigRow>
            </ConfigCard>
          </ContentSection>

          <ContentSection style={{ marginBottom: 24 }}>
            <SectionHeader>
              <h3>Work Settings</h3>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  workSettingsForm.setFieldsValue(workSettings);
                  setWorkSettingsModalVisible(true);
                }}
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
                loading={loadingWorkSettings}
              >
                Update Work Settings
              </Button>
            </SectionHeader>

            <ConfigCard>
              <ConfigRow>
                <span className="label">Working Days per Month</span>
                <span className="value">
                  {loadingWorkSettings ? (
                    <Spin size="small" />
                  ) : (
                    <Tag color="blue" style={{ fontSize: 16, padding: "6px 16px" }}>
                      {workSettings.workingDays} days
                    </Tag>
                  )}
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Default working days for billing calculations
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">Working Hours per Day</span>
                <span className="value">
                  {loadingWorkSettings ? (
                    <Spin size="small" />
                  ) : (
                    <Tag color="blue" style={{ fontSize: 16, padding: "6px 16px" }}>
                      {workSettings.workingHours} hours
                    </Tag>
                  )}
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Default working hours for billing calculations
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">Paid Leave per Month</span>
                <span className="value">
                  {loadingWorkSettings ? (
                    <Spin size="small" />
                  ) : (
                    <Tag color="magenta" style={{ fontSize: 16, padding: "6px 16px" }}>
                      {workSettings.paidLeave} day(s)
                    </Tag>
                  )}
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Default paid leave days per month for billing calculations
                  </span>
                </span>
              </ConfigRow>
            </ConfigCard>
          </ContentSection>

          <ContentSection>
            <SectionHeader>
              <h3>Default Currency & Exchange Rate</h3>
              <Button
                icon={<SyncOutlined spin={loadingRate} />}
                onClick={fetchExchangeRate}
                disabled={loadingRate}
              >
                Refresh Rate
              </Button>
            </SectionHeader>

            <ConfigCard>
              <ConfigRow>
                <span className="label">Default Currency</span>
                <span className="value">
                  <Tag color="purple" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {globalConfig.defaultCurrency}
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Primary currency for all transactions
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">USD to INR Exchange Rate</span>
                <span className="value">
                  {loadingRate ? (
                    <Spin size="small" />
                  ) : (
                    <Tag color="cyan" style={{ fontSize: 16, padding: "6px 16px" }}>
                      1 USD = ₹{exchangeRate || '---'}
                    </Tag>
                  )}
                  <span style={{ color: "#666", fontSize: 13 }}>
                    {lastUpdated ? `Last updated: ${lastUpdated}` : 'Fetching from Frankfurter API...'}
                  </span>
                </span>
              </ConfigRow>
            </ConfigCard>
          </ContentSection>


          <ContentSection>
            <SectionHeader>
              <h3>Email Settings</h3>
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={() => {
                  emailForm.setFieldsValue(emailSettings);
                  setEmailSettingsModalVisible(true);
                }}
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
              >
                Configure Email
              </Button>
            </SectionHeader>
            <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
              Configure email notifications for the HireNXT team. This does not apply to Client or Partner communications.
            </p>

            <ConfigCard>
              <ConfigRow>
                <span className="label">SMTP Server</span>
                <span className="value">
                  <Tag color="geekblue" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {emailSettings.smtpHost}:{emailSettings.smtpPort}
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Mail server configuration
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">Sender Email</span>
                <span className="value">
                  <Tag color="geekblue" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {emailSettings.senderEmail || "Not configured"}
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    {emailSettings.senderName}
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">HireNXT Team Email</span>
                <span className="value">
                  <Tag color="cyan" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {emailSettings.talentTeamEmail || "Not configured"}
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Interview notifications will be sent to this email
                  </span>
                </span>
              </ConfigRow>
            </ConfigCard>

            <div style={{ marginTop: 16 }}>
              <h4 style={{ color: "#014c75", marginBottom: 12 }}>Notification Types</h4>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <Tag color={emailSettings.interviewSchedulingEnabled ? "green" : "default"} style={{ padding: "6px 12px" }}>
                  {emailSettings.interviewSchedulingEnabled ? "✓" : "✗"} Interview Scheduling Alerts
                </Tag>
                <Tag color={emailSettings.hiringAlertEnabled ? "green" : "default"} style={{ padding: "6px 12px" }}>
                  {emailSettings.hiringAlertEnabled ? "✓" : "✗"} Hiring Alerts
                </Tag>
                <Tag color={emailSettings.jobAlertEnabled ? "green" : "default"} style={{ padding: "6px 12px" }}>
                  {emailSettings.jobAlertEnabled ? "✓" : "✗"} Job Related Alerts
                </Tag>
              </div>
            </div>
          </ContentSection>

          <ContentSection style={{ marginTop: 24 }}>
            <SectionHeader>
              <h3>Automation Settings</h3>
              <Button
                type="primary"
                icon={<CalendarOutlined />}
                onClick={() => {
                  automationForm.setFieldsValue(automationSettings);
                  setAutomationModalVisible(true);
                }}
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
              >
                Configure Automation
              </Button>
            </SectionHeader>
            <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
              Configure automatic timesheet generation and invoice settings.
            </p>

            <ConfigCard>
              <ConfigRow>
                <span className="label">Timesheet Generation Day</span>
                <span className="value">
                  <Tag color="blue" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {automationSettings.timesheetGenerateDay === 1 ? "1st" : 
                     automationSettings.timesheetGenerateDay === 2 ? "2nd" :
                     automationSettings.timesheetGenerateDay === 3 ? "3rd" :
                     `${automationSettings.timesheetGenerateDay}th`} of each month
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Timesheets are auto-generated on this day
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">Invoice Due Days</span>
                <span className="value">
                  <Tag color="orange" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {automationSettings.invoiceDueDays} days
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Number of days after invoice date for payment due
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">Tax Percentage</span>
                <span className="value">
                  <Tag color="purple" style={{ fontSize: 16, padding: "6px 16px" }}>
                    {automationSettings.taxPercentage}%
                  </Tag>
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Applied to invoice subtotal
                  </span>
                </span>
              </ConfigRow>
            </ConfigCard>
          </ContentSection>

          <ContentSection style={{ marginTop: 24 }}>
            <SectionHeader>
              <h3>Offline Partner Relations</h3>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  offlinePartnerForm.setFieldsValue(offlinePartnerSettings);
                  setOfflinePartnerModalVisible(true);
                }}
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
                loading={loadingOfflinePartnerSettings}
              >
                Update Offline Partner Settings
              </Button>
            </SectionHeader>
            <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
              Configure default markup and deduction rates applied when customers work with offline (non-platform) partners.
            </p>
            <ConfigCard>
              <ConfigRow>
                <span className="label">Offline Partner Markup</span>
                <span className="value">
                  {loadingOfflinePartnerSettings ? (
                    <Spin size="small" />
                  ) : (
                    <Tag color="green" style={{ fontSize: 16, padding: "6px 16px" }}>
                      {offlinePartnerSettings.offlinePartnerMarkup}%
                    </Tag>
                  )}
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Added to customer billing for offline partner engagements
                  </span>
                </span>
              </ConfigRow>
              <ConfigRow>
                <span className="label">Offline Partner Deduction</span>
                <span className="value">
                  {loadingOfflinePartnerSettings ? (
                    <Spin size="small" />
                  ) : (
                    <Tag color="orange" style={{ fontSize: 16, padding: "6px 16px" }}>
                      {offlinePartnerSettings.offlinePartnerDeduction}%
                    </Tag>
                  )}
                  <span style={{ color: "#666", fontSize: 13 }}>
                    Deducted from payout for offline partner arrangements
                  </span>
                </span>
              </ConfigRow>
            </ConfigCard>
          </ContentSection>
        </div>
      )}

      {activeTab === "Client" && (
        <ContentSection>
          <SectionHeader>
            <h3>Client Specific Configurations</h3>
          </SectionHeader>
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Configure custom settings for specific clients. Values default to global settings if not customized.
          </p>
          <TableContainer>
            <Table
              columns={clientColumns}
              dataSource={clientConfigs}
              rowKey="id"
              pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
                pageSize: 10,
                showTotal: (total) => `Total ${total} clients`,
              }}
            />
          </TableContainer>
        </ContentSection>
      )}

      {activeTab === "Partner" && (
        <ContentSection>
          <SectionHeader>
            <h3>Partner Specific Configuration</h3>
          </SectionHeader>
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Configure custom settings for specific partners. Values default to global settings if not customized.
          </p>
          <TableContainer>
            <Table
              columns={partnerColumns}
              dataSource={partnerConfigs}
              rowKey="id"
              pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
                pageSize: 10,
                showTotal: (total) => `Total ${total} partners`,
              }}
            />
          </TableContainer>
        </ContentSection>
      )}

      {activeTab === "RolesPricing" && (
        <ContentSection>
          <SectionHeader>
            <h3>Role Pricing</h3>
            {rolePricingSubTab === "RolePricing" && (
              <div style={{ display: "flex", gap: 8 }}>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>Download Template</Button>
                <label>
                  <Button icon={<UploadOutlined />} loading={bulkUploadLoading} onClick={() => document.getElementById("rp-bulk-upload-input").click()}>Bulk Upload</Button>
                  <input id="rp-bulk-upload-input" type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleBulkUpload} />
                </label>
                <Button icon={<SyncOutlined />} loading={seedingRoles} onClick={handleSeedRoles}>Seed Roles</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRoleRecord(null); rolePricingForm.resetFields(); rolePricingForm.setFieldsValue({ country: getReferenceDefault("country", "India"), currency: getReferenceDefault("currency", "INR"), workMode: getReferenceDefault("workMode", "Remote"), geoTier: getReferenceDefault("geoTier", "Any"), marketType: getReferenceDefault("marketType", "Staff Augmentation"), confidenceScore: 70, status: "active" }); setAddRoleModalVisible(true); }} style={{ background: "#00d9a9", borderColor: "#00d9a9" }}>Add Role Pricing</Button>
              </div>
            )}
          </SectionHeader>
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Maintain advisory market rate ranges by role, experience, location, geo tier, work mode, market type, and currency. These records guide partner pricing nudges and AI budget suggestions, but never block partners or customers from entering their chosen price.
          </p>
          <TabsContainer style={{ marginBottom: 16 }}>
            <button
              className={`tab-button ${rolePricingSubTab === "RolePricing" ? "active" : ""}`}
              onClick={() => setRolePricingSubTab("RolePricing")}
            >
              Role Pricing
            </button>
            <button
              className={`tab-button ${rolePricingSubTab === "Benchmark" ? "active" : ""}`}
              onClick={() => setRolePricingSubTab("Benchmark")}
            >
              Benchmark
            </button>
          </TabsContainer>
          {rolePricingSubTab === "Benchmark" && (
            <ConfigCard style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
                <div>
                  <h4 style={{ margin: 0, color: "#014c75" }}>Dropdown Options Used by Benchmarks</h4>
                  <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
                    These are the selectable options used when adding or filtering role pricing records. They are not prices themselves.
                  </p>
                </div>
                <Button icon={<PlusOutlined />} onClick={() => handleOpenReferenceModal()} loading={loadingPricingReferences}>
                  Add Reference Value
                </Button>
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <Input
                  placeholder="Search benchmark options..."
                  prefix={<SearchOutlined />}
                  style={{ width: 260 }}
                  value={referenceSearch}
                  onChange={(e) => { setReferenceSearch(e.target.value); setReferencePage(1); }}
                  allowClear
                  onClear={() => { setReferenceSearch(""); setReferencePage(1); }}
                />
                <Select
                  placeholder="Category"
                  style={{ width: 220 }}
                  allowClear
                  value={referenceTypeFilter || undefined}
                  onChange={(value) => { setReferenceTypeFilter(value || ""); setReferencePage(1); }}
                >
                  {Object.entries(PRICING_REFERENCE_TYPE_LABELS).map(([type, label]) => (
                    <Option key={type} value={type}>{label}</Option>
                  ))}
                </Select>
                <Button onClick={() => {
                  setReferenceSearch("");
                  setReferenceTypeFilter("");
                  setReferencePage(1);
                }}>
                  Clear Filters
                </Button>
              </div>
              <Table
                size="small"
                loading={loadingPricingReferences}
                dataSource={filteredPricingReferenceRows}
                rowKey={(record) => record.id || `${record.type}-${record.value}`}
                pagination={{
                  current: referencePage,
                  pageSize: referencePageSize,
                  total: filteredPricingReferenceRows.length,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} options`,
                  onChange: (page, pageSize) => {
                    setReferencePage(page);
                    setReferencePageSize(pageSize);
                  },
                }}
                columns={[
                  { title: "Type", dataIndex: "type", key: "type", width: 140, render: (type) => PRICING_REFERENCE_TYPE_LABELS[type] || type },
                  { title: "Label", dataIndex: "label", key: "label" },
                  { title: "Value", dataIndex: "value", key: "value" },
                  { title: "Sort", dataIndex: "sortOrder", key: "sortOrder", width: 80 },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 120,
                    render: (_, record) => (
                      <div style={{ display: "flex", gap: 8 }}>
                        <Button size="small" icon={<EditOutlined />} disabled={!record.id} onClick={() => handleOpenReferenceModal(record)} />
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={!record.id} onClick={() => { if (window.confirm("Delete this reference value?")) handleDeleteReferenceValue(record); }} />
                      </div>
                    ),
                  },
                ]}
              />
            </ConfigCard>
          )}
          {rolePricingSubTab === "RolePricing" && (
            <>
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <Input
                  placeholder="Search by role name..."
                  prefix={<SearchOutlined />}
                  style={{ width: 260 }}
                  value={rolePricingSearch}
                  onChange={(e) => setRolePricingSearch(e.target.value)}
                  onPressEnter={() => fetchRolePricing(1)}
                  allowClear
                  onClear={() => { setRolePricingSearch(""); fetchRolePricing(1, { search: "" }); }}
                />
                <Input
                  placeholder="Category..."
                  style={{ width: 160 }}
                  value={rolePricingCategory}
                  onChange={(e) => setRolePricingCategory(e.target.value)}
                  onPressEnter={() => fetchRolePricing(1)}
                  allowClear
                />
                <Select
                  placeholder="Work Mode"
                  style={{ width: 160 }}
                  allowClear
                  value={rolePricingWorkMode || undefined}
                  onChange={(v) => { setRolePricingWorkMode(v || ""); fetchRolePricing(1, { workMode: v || "" }); }}
                >
                  {renderReferenceOptions("workMode")}
                </Select>
                <AutoComplete
                  placeholder="City"
                  style={{ width: 160 }}
                  allowClear
                  options={getReferenceAutoCompleteOptions("city")}
                  value={rolePricingCity}
                  onChange={(v) => {
                    setRolePricingCity(v || "");
                    if (!v) fetchRolePricing(1, { city: "" });
                  }}
                  onSelect={(v) => fetchRolePricing(1, { city: v || "" })}
                  onBlur={() => fetchRolePricing(1, { city: rolePricingCity || "" })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") fetchRolePricing(1, { city: rolePricingCity || "" });
                  }}
                />
                <AutoComplete
                  placeholder="Country"
                  style={{ width: 150 }}
                  allowClear
                  options={getReferenceAutoCompleteOptions("country")}
                  value={rolePricingCountry}
                  onChange={(v) => {
                    setRolePricingCountry(v || "");
                    if (!v) fetchRolePricing(1, { country: "" });
                  }}
                  onSelect={(v) => fetchRolePricing(1, { country: v || "" })}
                  onBlur={() => fetchRolePricing(1, { country: rolePricingCountry || "" })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") fetchRolePricing(1, { country: rolePricingCountry || "" });
                  }}
                />
                <Select placeholder="Geo Tier" style={{ width: 140 }} allowClear value={rolePricingGeoTier || undefined} onChange={(v) => { setRolePricingGeoTier(v || ""); fetchRolePricing(1, { geoTier: v || "" }); }}>
                  {renderReferenceOptions("geoTier")}
                </Select>
                <Select placeholder="Market Type" style={{ width: 180 }} allowClear value={rolePricingMarketType || undefined} onChange={(v) => { setRolePricingMarketType(v || ""); fetchRolePricing(1, { marketType: v || "" }); }}>
                  {renderReferenceOptions("marketType")}
                </Select>
                <Select placeholder="Source" style={{ width: 140 }} allowClear value={rolePricingSource || undefined} onChange={(v) => { setRolePricingSource(v || ""); fetchRolePricing(1, { source: v || "" }); }}>
                  <Option value="admin">Admin</Option>
                  <Option value="llm_generated">LLM Draft</Option>
                </Select>
                <Select placeholder="Confidence" style={{ width: 150 }} allowClear value={rolePricingConfidence || undefined} onChange={(v) => { setRolePricingConfidence(v || ""); fetchRolePricing(1, { confidence: v || "" }); }}>
                  <Option value="high">High</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="low">Low</Option>
                </Select>
                <Button onClick={() => fetchRolePricing(1)}>Search</Button>
              </div>
              <TableContainer>
                <Table
                  loading={loadingRolePricing}
                  dataSource={rolePricingRecords}
                  rowKey="id"
                  columns={[
                    { title: "Role Name", dataIndex: "roleName", key: "roleName", width: 190, render: (v, r) => <div><strong>{v}</strong><div style={{ fontSize: 12, color: "#666" }}>{(r.aliases || []).slice(0, 2).join(", ")}</div></div> },
                    { title: "Category", dataIndex: "category", key: "category", width: 120 },
                    { title: "Exp. Range (yrs)", key: "expRange", width: 130, render: (_, r) => `${r.experienceMin}–${r.experienceMax}` },
                    { title: "Work Mode", dataIndex: "workMode", key: "workMode", width: 110 },
                    { title: "City", dataIndex: "city", key: "city", width: 120 },
                    { title: "Geo Tier", dataIndex: "geoTier", key: "geoTier", width: 100 },
                    { title: "Market", dataIndex: "marketType", key: "marketType", width: 150 },
                    { title: "Country", dataIndex: "country", key: "country", width: 100 },
                    { title: "Currency", dataIndex: "currency", key: "currency", width: 100 },
                    { title: "Min Rate", dataIndex: "minRate", key: "minRate", width: 120, render: (v) => Number(v).toLocaleString("en-IN") },
                    { title: "Max Rate", dataIndex: "maxRate", key: "maxRate", width: 120, render: (v) => Number(v).toLocaleString("en-IN") },
                    { title: "Confidence", dataIndex: "confidenceScore", key: "confidenceScore", width: 110, render: (v) => <Tag color={v >= 80 ? "green" : v >= 50 ? "gold" : "red"}>{v || 0}%</Tag> },
                    { title: "Source", dataIndex: "source", key: "source", width: 120, render: (v) => v === "llm_generated" ? <Tag color="blue">LLM Draft</Tag> : <Tag color="green">Admin</Tag> },
                    {
                      title: "Actions", key: "actions", width: 100,
                      render: (_, r) => (
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditRoleRecord(r); rolePricingForm.setFieldsValue({ ...r, experienceMin: r.experienceMin, experienceMax: r.experienceMax }); setAddRoleModalVisible(true); }} />
                          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => { if (window.confirm("Delete this record?")) handleDeleteRoleRecord(r.id); }} />
                        </div>
                      ),
                    },
                  ]}
                  pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
                    current: rolePricingPage,
                    pageSize: rolePricingPageSize,
                    total: rolePricingTotal,
                    showTotal: (total) => `Total ${total} records`,
                    onChange: (page, pageSize) => {
                      setRolePricingPageSize(pageSize);
                      fetchRolePricing(page, { limit: pageSize });
                    },
                  }}
                />
              </TableContainer>
            </>
          )}
        </ContentSection>
      )}

      {activeTab === "OtherSettings" && (
        <ContentSection>
          <SectionHeader>
            <h3>Other Settings</h3>
          </SectionHeader>
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Manage platform-wide configuration options such as supported countries and cities shown in location dropdowns.
          </p>
          <TabsContainer style={{ marginBottom: 16 }}>
            <button
              className={`tab-button ${otherSettingsSubTab === "CountriesCities" ? "active" : ""}`}
              onClick={() => setOtherSettingsSubTab("CountriesCities")}
            >
              Countries &amp; Cities
            </button>
          </TabsContainer>

          {otherSettingsSubTab === "CountriesCities" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <span style={{ fontSize: 13, color: "#888" }}>
                    First time setup? Seed all default countries, cities, and dropdown values into the database.
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={seedingReferenceValues}
                    onClick={handleSeedReferenceValues}
                  >
                    Seed Default Values
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => { setNewCountryName(""); setAddCountryModalVisible(true); }}
                    style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
                  >
                    Add Country
                  </Button>
                </div>
              </div>

              {locationsData.countries
                .filter((c) => c.value !== "Any")
                .map((country) => {
                  const countryKey = country.id || country.value;
                  const isExpanded = expandedCountries.has(countryKey);
                  const isRemote = country.value === "Remote";
                  const cities = isRemote
                    ? []
                    : locationsData.allCities.filter((c) => c.parentValue === country.value && c.value !== "Remote" && c.value !== "Any");
                  const cityCount = cities.length + 1;

                  return (
                  <div
                    key={countryKey}
                    style={{
                      border: "1px solid #e8e8e8",
                      borderRadius: 8,
                      marginBottom: 12,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background: isExpanded ? "#e8f4ff" : "#f5f7fa",
                        borderBottom: isExpanded ? "1px solid #d0e8ff" : "none",
                        padding: "12px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() => toggleCountry(countryKey)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {isExpanded
                          ? <DownOutlined style={{ fontSize: 11, color: "#1890ff" }} />
                          : <RightOutlined style={{ fontSize: 11, color: "#888" }} />
                        }
                        <EnvironmentOutlined style={{ color: "#1890ff", fontSize: 14 }} />
                        <span style={{ fontWeight: 600, fontSize: 14, color: "#014c75" }}>
                          {country.value}
                        </span>
                        <Tag style={{ fontSize: 11, marginLeft: 2 }} color="default">
                          {isRemote ? "1 location" : `${cityCount} location${cityCount !== 1 ? "s" : ""}`}
                        </Tag>
                      </div>
                      <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        {!isRemote && (
                          <>
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setEditingCountryRecord(country);
                                setEditCountryName(country.value);
                                setEditCountryModalVisible(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteReferenceValue({ ...country, type: "country" })}
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 16px",
                          borderBottom: "1px solid #f0f0f0",
                          background: "#fff",
                        }}>
                          <span style={{ fontSize: 13, color: "#555" }}>Remote</span>
                          <Tag color="blue" style={{ fontSize: 11 }}>Default</Tag>
                        </div>

                        {!isRemote && cities.map((city) => (
                          <div
                            key={city.id || city.value}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "10px 16px",
                              borderBottom: "1px solid #f0f0f0",
                              background: "#fff",
                            }}
                          >
                            <span style={{ fontSize: 13, color: "#333" }}>{city.value}</span>
                            <div style={{ display: "flex", gap: 6 }}>
                              <Button
                                size="small"
                                type="text"
                                icon={<EditOutlined />}
                                style={{ color: "#1890ff" }}
                                onClick={() => {
                                  setEditingCityRecord(city);
                                  setEditCityName(city.value);
                                  setEditCityModalVisible(true);
                                }}
                              />
                              <Button
                                size="small"
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteReferenceValue({ ...city, type: "city" })}
                              />
                            </div>
                          </div>
                        ))}

                        {!isRemote && cities.length === 0 && (
                          <div style={{ padding: "12px 16px", color: "#aaa", fontSize: 13, fontStyle: "italic", background: "#fff" }}>
                            No cities added yet.
                          </div>
                        )}

                        {!isRemote && (
                          <div style={{ padding: "10px 16px", background: "#fafafa" }}>
                            <Button
                              type="dashed"
                              size="small"
                              icon={<PlusOutlined />}
                              style={{ width: "100%" }}
                              onClick={() => {
                                setSelectedCountry(country.value);
                                setNewCityName("");
                                setAddCityModalVisible(true);
                              }}
                            >
                              Add City to {country.value}
                            </Button>
                          </div>
                        )}

                        {isRemote && (
                          <div style={{ padding: "12px 16px", color: "#aaa", fontSize: 12, background: "#fff" }}>
                            Remote is a fixed location — it cannot be modified or removed.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })
              }

              {locationsData.countries.filter((c) => c.value !== "Any").length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
                  No countries configured. Click "Add Country" to get started.
                </div>
              )}
            </div>
          )}
        </ContentSection>
      )}

      <Modal
        title={editRoleRecord ? "Edit Role Pricing" : "Add Role Pricing"}
        open={addRoleModalVisible}
        onOk={handleSaveRoleRecord}
        onCancel={() => { setAddRoleModalVisible(false); setEditRoleRecord(null); rolePricingForm.resetFields(); }}
        confirmLoading={savingRole}
        okText={editRoleRecord ? "Update" : "Add"}
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
        width={680}
      >
        <Form form={rolePricingForm} layout="vertical" style={{ marginTop: 12 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="roleName" label="Role Name" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g., React Developer" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="Category" initialValue="General">
                <Input placeholder="e.g., Frontend, Backend, Data" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="aliases" label="Aliases">
                <Select mode="tags" placeholder="Add aliases like Frontend Engineer" tokenSeparators={[","]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="skillTags" label="Skill Tags">
                <Select mode="tags" placeholder="Add skills like React, TypeScript" tokenSeparators={[","]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="experienceMin" label="Experience Min (years)" rules={[{ required: true, message: "Required" }]}>
                <InputNumber style={{ width: "100%" }} min={0} max={40} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="experienceMax" label="Experience Max (years)" rules={[{ required: true, message: "Required" }]}>
                <InputNumber style={{ width: "100%" }} min={0} max={40} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="workMode" label="Work Mode" rules={[{ required: true, message: "Required" }]}>
                <Select placeholder="Select">
                  {renderReferenceOptions("workMode")}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="experienceBucket" label="Experience Bucket">
                <Select placeholder="Select">
                  <Option value="Junior">Junior</Option>
                  <Option value="Mid">Mid</Option>
                  <Option value="Senior">Senior</Option>
                  <Option value="Lead">Lead</Option>
                  <Option value="General">General</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="geoTier" label="Geo Tier" initialValue="Any">
                <Select>
                  {renderReferenceOptions("geoTier")}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="marketType" label="Market Type" initialValue="Staff Augmentation">
                <Select>
                  {renderReferenceOptions("marketType")}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="minRate" label="Min Rate (₹)" rules={[{ required: true, message: "Required" }]}>
                <InputNumber style={{ width: "100%" }} min={0} step={1000} formatter={(v) => v?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} parser={(v) => v.replace(/,/g, "")} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxRate" label="Max Rate (₹)" rules={[{ required: true, message: "Required" }]}>
                <InputNumber style={{ width: "100%" }} min={0} step={1000} formatter={(v) => v?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} parser={(v) => v.replace(/,/g, "")} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label="Currency" initialValue="INR">
                <Select>
                  {renderReferenceOptions("currency")}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="country" label="Country" initialValue="India">
                <Select showSearch placeholder="Select country">
                  {renderReferenceOptions("country")}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="city" label="City" rules={[{ required: true, message: "Required" }]}>
                <Select showSearch placeholder="Select city">
                  {renderReferenceOptions("city")}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="confidenceScore" label="Confidence Score" initialValue={70}>
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="draft">Draft</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="similarRoles" label="Similar Roles">
                <Select mode="tags" placeholder="Related roles" tokenSeparators={[","]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Reference notes, source details, or assumptions" />
          </Form.Item>
          <p style={{ margin: 0, color: "#666", fontSize: 12 }}>
            Benchmarks are guidance only. They improve suggestions and nudges, but users can still save any price.
          </p>
        </Form>
      </Modal>

      <Modal
        title={editingReference ? "Edit Reference Value" : "Add Reference Value"}
        open={referenceModalVisible}
        onOk={handleSaveReferenceValue}
        onCancel={() => { setReferenceModalVisible(false); setEditingReference(null); referenceForm.resetFields(); }}
        confirmLoading={savingReference}
        okText={editingReference ? "Update" : "Add"}
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
        width={520}
      >
        <Form form={referenceForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="type" label="Reference Type" rules={[{ required: true, message: "Required" }]}>
            <Select disabled={!!editingReference}>
              {Object.entries(PRICING_REFERENCE_TYPE_LABELS).map(([type, label]) => (
                <Option key={type} value={type}>{label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="value" label="Stored Value" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g., Remote, INR, Tier 1" />
          </Form.Item>
          <Form.Item name="label" label="Display Label">
            <Input placeholder="Optional display label" />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort Order">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <p style={{ margin: 0, color: "#666", fontSize: 12 }}>
            Deleting is blocked when a value is still used by pricing benchmarks. If all custom values are deleted, built-in defaults remain available.
          </p>
        </Form>
      </Modal>

      <Modal
        title="Update Global Configuration"
        open={globalConfigModalVisible}
        onOk={handleUpdateGlobalConfig}
        onCancel={() => {
          setGlobalConfigModalVisible(false);
          globalForm.resetFields();
        }}
        width={600}
        okText="Update"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        <Form form={globalForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="clientMarkup"
                label="Client Markup (%)"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="partnerDeduction"
                label="Partner Deduction (%)"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Update Work Settings"
        open={workSettingsModalVisible}
        onOk={handleUpdateWorkSettings}
        onCancel={() => {
          setWorkSettingsModalVisible(false);
          workSettingsForm.resetFields();
        }}
        width={600}
        okText="Update"
        okButtonProps={{ 
          style: { background: "#00d9a9", borderColor: "#00d9a9" },
          loading: savingWorkSettings
        }}
      >
        <Form form={workSettingsForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="workingDays"
                label="Working Days per Month"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber style={{ width: "100%" }} min={1} max={31} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="workingHours"
                label="Working Hours per Day"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber style={{ width: "100%" }} min={1} max={24} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="paidLeave"
                label="Paid Leave per Month"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} max={31} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ padding: 12, background: "#f0f5ff", borderRadius: 6, fontSize: 13 }}>
            <strong>Note:</strong> These settings apply globally to all organizations unless overridden by organization-specific settings in the Client/Partner tabs.
          </div>
        </Form>
      </Modal>

      <Modal
        title={`Edit Configuration - ${selectedClient?.clientName || ""}`}
        open={clientEditModalVisible}
        onOk={handleUpdateClient}
        onCancel={() => {
          setClientEditModalVisible(false);
          setSelectedClient(null);
          clientForm.resetFields();
        }}
        width={500}
        okText="Update"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        {selectedClient && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
              <div><strong>Client:</strong> {selectedClient.clientName}</div>
              <div><strong>Location:</strong> {selectedClient.location}</div>
            </div>
            <Form form={clientForm} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="markup" label="Markup (%)">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace("%", "")}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="currency" label="Currency">
                    <Select>
                      <Option value="USD">USD</Option>
                      <Option value="INR">INR</Option>
                      <Option value="EUR">EUR</Option>
                      <Option value="GBP">GBP</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="workingDays" label="Working Days">
                    <InputNumber style={{ width: "100%" }} min={1} max={31} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="workingHours" label="Working Hours">
                    <InputNumber style={{ width: "100%" }} min={1} max={24} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="paidLeave" label="Paid Leave per Month">
                <InputNumber style={{ width: "100%" }} min={0} max={31} />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      <Modal
        title={`Edit Configuration - ${selectedPartner?.partnerName || ""}`}
        open={partnerEditModalVisible}
        onOk={handleUpdatePartner}
        onCancel={() => {
          setPartnerEditModalVisible(false);
          setSelectedPartner(null);
          partnerForm.resetFields();
        }}
        width={500}
        okText="Update"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        {selectedPartner && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
              <div><strong>Partner:</strong> {selectedPartner.partnerName}</div>
              <div><strong>Location:</strong> {selectedPartner.location}</div>
            </div>
            <Form form={partnerForm} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="deduction" label="Deduction (%)">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace("%", "")}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="currency" label="Currency">
                    <Select>
                      <Option value="USD">USD</Option>
                      <Option value="INR">INR</Option>
                      <Option value="EUR">EUR</Option>
                      <Option value="GBP">GBP</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="workingDays" label="Working Days">
                    <InputNumber style={{ width: "100%" }} min={1} max={31} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="workingHours" label="Working Hours">
                    <InputNumber style={{ width: "100%" }} min={1} max={24} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        )}
      </Modal>

      <Modal
        title="Email Settings Configuration"
        open={emailSettingsModalVisible}
        onOk={handleUpdateEmailSettings}
        onCancel={() => {
          setEmailSettingsModalVisible(false);
          emailForm.resetFields();
        }}
        width={600}
        okText="Update"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        <Form form={emailForm} layout="vertical" style={{ marginTop: 16 }}>
          <p style={{ color: "#666", marginBottom: 16, fontSize: 13 }}>
            Configure email settings for HireNXT team notifications. These notifications are used for internal alerts only.
          </p>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="smtpHost"
                label="SMTP Host"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="smtp.example.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="smtpPort"
                label="SMTP Port"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber style={{ width: "100%" }} min={1} max={65535} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="senderEmail"
                label="Sender Email (Gmail)"
                rules={[{ required: true, message: "Required" }, { type: "email", message: "Invalid email" }]}
              >
                <Input placeholder="your-email@gmail.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="senderName"
                label="Sender Name"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="HireNXT Notifications" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="talentTeamEmail"
                label="HireNXT Team Email (Receives Interview Notifications)"
                rules={[{ required: true, message: "Required" }, { type: "email", message: "Invalid email" }]}
              >
                <Input placeholder="talent-team@hirenxt.com" />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ marginTop: 8, marginBottom: 8 }}>
            <h4 style={{ color: "#014c75", marginBottom: 12 }}>Notification Types</h4>
          </div>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="interviewSchedulingEnabled"
                label="Interview Scheduling"
                valuePropName="checked"
              >
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="hiringAlertEnabled"
                label="Hiring Alerts"
                valuePropName="checked"
              >
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="jobAlertEnabled"
                label="Job Alerts"
                valuePropName="checked"
              >
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Automation Settings Configuration"
        open={automationModalVisible}
        onOk={handleUpdateAutomationSettings}
        onCancel={() => {
          setAutomationModalVisible(false);
          automationForm.resetFields();
        }}
        width={500}
        okText="Update"
        okButtonProps={{ 
          style: { background: "#00d9a9", borderColor: "#00d9a9" },
          loading: savingAutomationSettings
        }}
      >
        <Form form={automationForm} layout="vertical" style={{ marginTop: 16 }}>
          <p style={{ color: "#666", marginBottom: 16, fontSize: 13 }}>
            Configure automatic timesheet generation and invoice settings for the platform.
          </p>
          <Form.Item
            name="timesheetGenerateDay"
            label="Timesheet Generation Day (Day of Month)"
            rules={[{ required: true, message: "Required" }]}
            tooltip="Timesheets will be automatically generated on this day of each month"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={28}
              placeholder="1-28"
            />
          </Form.Item>
          <Form.Item
            name="invoiceDueDays"
            label="Invoice Due Days"
            rules={[{ required: true, message: "Required" }]}
            tooltip="Number of days after invoice date that payment is due"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={90}
              formatter={(value) => `${value} days`}
              parser={(value) => value.replace(' days', '')}
            />
          </Form.Item>
          <Form.Item
            name="taxPercentage"
            label="Tax Percentage (%)"
            rules={[{ required: true, message: "Required" }]}
            tooltip="Tax percentage applied to invoice subtotal"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Offline Partner Relations Settings"
        open={offlinePartnerModalVisible}
        onOk={handleUpdateOfflinePartnerSettings}
        onCancel={() => {
          setOfflinePartnerModalVisible(false);
          offlinePartnerForm.resetFields();
        }}
        width={500}
        okText="Update"
        okButtonProps={{
          style: { background: "#00d9a9", borderColor: "#00d9a9" },
          loading: savingOfflinePartnerSettings,
        }}
      >
        <Form form={offlinePartnerForm} layout="vertical" style={{ marginTop: 16 }}>
          <p style={{ color: "#666", marginBottom: 16, fontSize: 13 }}>
            Configure default markup and deduction rates for offline (non-platform) partner engagements.
          </p>
          <Form.Item
            name="offlinePartnerMarkup"
            label="Offline Partner Markup (%)"
            rules={[{ required: true, message: "Required" }]}
            tooltip="Markup percentage added to customer billing for offline partner engagements"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          </Form.Item>
          <Form.Item
            name="offlinePartnerDeduction"
            label="Offline Partner Deduction (%)"
            rules={[{ required: true, message: "Required" }]}
            tooltip="Deduction percentage applied to payout for offline partner arrangements"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Add City to ${selectedCountry}`}
        open={addCityModalVisible}
        onOk={handleAddCity}
        onCancel={() => { setAddCityModalVisible(false); setNewCityName(""); }}
        okButtonProps={{ loading: savingCity, style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
        okText="Add City"
      >
        <p style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
          Enter the city name to add it to the {selectedCountry} location options across the platform.
        </p>
        <Input
          placeholder="e.g., Bangalore"
          value={newCityName}
          onChange={(e) => setNewCityName(e.target.value)}
          onPressEnter={handleAddCity}
          maxLength={50}
        />
      </Modal>

      <Modal
        title="Add Country"
        open={addCountryModalVisible}
        onOk={handleAddCountry}
        onCancel={() => { setAddCountryModalVisible(false); setNewCountryName(""); }}
        okButtonProps={{ loading: savingCountry, style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
        okText="Add Country"
      >
        <p style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
          Enter the country name. You can add cities under it after it is created.
        </p>
        <Input
          placeholder="e.g., United States"
          value={newCountryName}
          onChange={(e) => setNewCountryName(e.target.value)}
          onPressEnter={handleAddCountry}
          maxLength={60}
        />
      </Modal>

      <Modal
        title="Edit Country"
        open={editCountryModalVisible}
        onOk={handleEditCountry}
        onCancel={() => { setEditCountryModalVisible(false); setEditingCountryRecord(null); setEditCountryName(""); }}
        okButtonProps={{ loading: savingEditCountry, style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
        okText="Save Changes"
      >
        <p style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
          Update the country name. This will reflect across all location dropdowns.
        </p>
        <Input
          placeholder="Country name"
          value={editCountryName}
          onChange={(e) => setEditCountryName(e.target.value)}
          onPressEnter={handleEditCountry}
          maxLength={60}
        />
      </Modal>

      <Modal
        title="Edit City"
        open={editCityModalVisible}
        onOk={handleEditCity}
        onCancel={() => { setEditCityModalVisible(false); setEditingCityRecord(null); setEditCityName(""); }}
        okButtonProps={{ loading: savingEditCity, style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
        okText="Save Changes"
      >
        <p style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
          Update the city name. This will reflect across all location dropdowns.
        </p>
        <Input
          placeholder="City name"
          value={editCityName}
          onChange={(e) => setEditCityName(e.target.value)}
          onPressEnter={handleEditCity}
          maxLength={50}
        />
      </Modal>
      {activeTab === "ComplianceTemplates" && (
        <ComplianceTemplatesSection />
      )}
    </SettingsContainer>
  );
};

const DOC_TYPE_LABELS_MAP = { nda: "NDA", contract: "Contract Agreement", declaration: "Talent Declaration" };

// Convert plain text (legacy DB content) to HTML for the rich-text editor
const legacyTextToHtml = (text) => {
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return text; // already HTML from react-quill
  return text
    .split(/\n\n+/)
    .map((para) => {
      const t = para.trim();
      if (!t) return '';
      return `<p>${t.replace(/\n/g, '<br />')}</p>`;
    })
    .filter(Boolean)
    .join('');
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet'];

const ComplianceTemplatesSection = () => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState({ open: false, content: "" });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewingTemplate, setPreviewingTemplate] = useState(false);
  const [editForm] = Form.useForm();
  const [editorContent, setEditorContent] = useState("");

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setFetchError(null);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(API_CONST.COMPLIANCE_TEMPLATES, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Unknown error";
      setFetchError(errMsg);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openEdit = (tpl) => {
    setEditingTemplate(tpl);
    editForm.setFieldsValue({ title: tpl.title, isActive: tpl.isActive });
    // Convert legacy plain-text content to HTML for the rich-text editor
    setEditorContent(legacyTextToHtml(tpl.content));
    setEditDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await editForm.validateFields(['title', 'isActive']);
      if (!editorContent || editorContent.replace(/<[^>]*>/g, '').trim() === '') {
        message.error("Template content cannot be empty");
        return;
      }
      setSavingTemplate(true);
      const token = localStorage.getItem("authToken");
      await axios.put(`${API_CONST.COMPLIANCE_TEMPLATES}/${editingTemplate.id}`, {
        ...values,
        content: editorContent,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Template saved successfully");
      setEditDrawerOpen(false);
      setEditingTemplate(null);
      setEditorContent("");
      fetchTemplates();
    } catch (err) {
      if (!err.errorFields) message.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewingTemplate(true);
      const token = localStorage.getItem("authToken");
      // Pass current editor HTML as the draft content so preview reflects unsaved changes
      const res = await axios.post(
        `${API_CONST.COMPLIANCE_TEMPLATES}/${editingTemplate.id}/preview`,
        { overrides: { _draftContent: editorContent || "" } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewModal({ open: true, content: res.data.rendered });
    } catch {
      message.error("Failed to generate preview");
    } finally {
      setPreviewingTemplate(false);
    }
  };

  const PLACEHOLDERS = [
    "{{TALENT_NAME}}", "{{AADHAAR_NUMBER}}", "{{JOB_TITLE}}", "{{CUSTOMER_ORG}}",
    "{{START_DATE}}", "{{CONTRACT_DURATION}}", "{{SIGNED_DATE}}", "{{SIGNED_LOCATION}}",
  ];

  return (
    <ContentSection>
      <SectionHeader>
        <h3>Compliance Document Templates</h3>
      </SectionHeader>
      <p style={{ color: "#666", fontSize: 14, marginBottom: 20 }}>
        Manage the three platform-wide compliance documents: NDA, Contract Agreement, and Talent Declaration.
        These are auto-generated when a hiring record is created and sent to the Partner for digital sign-off.
        All documents are branded with the HireNXT logo — no partner branding.
      </p>

      <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6, padding: "10px 16px", marginBottom: 20, fontSize: 13 }}>
        <strong>Available Placeholders:</strong>{" "}
        {PLACEHOLDERS.map((p) => (
          <Tag key={p} style={{ margin: "2px 4px", fontFamily: "monospace", fontSize: 12 }}>{p}</Tag>
        ))}
      </div>

      <Spin spinning={loadingTemplates}>
        {fetchError && (
          <div style={{
            background: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 8,
            padding: "16px 20px",
            marginBottom: 16,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}>
            <CloseOutlined style={{ color: "#ff4d4f", marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: "#cf1322", marginBottom: 4 }}>
                Failed to load compliance templates
              </div>
              <div style={{ fontSize: 13, color: "#595959", marginBottom: 10 }}>
                {fetchError.includes("does not exist") || fetchError.includes("relation") ? (
                  <>
                    The compliance document tables have not been created on this database yet.
                    Run the pending migrations to fix this:{" "}
                    <code style={{ background: "#f5f5f5", padding: "1px 6px", borderRadius: 3, fontSize: 12 }}>
                      DATABASE_URL=&lt;your-db-url&gt; npm --prefix HireNXT-Backend run db:migrate
                    </code>
                  </>
                ) : (
                  fetchError
                )}
              </div>
              <Button size="small" icon={<SyncOutlined />} onClick={fetchTemplates}>
                Retry
              </Button>
            </div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {!fetchError && templates.length === 0 && !loadingTemplates && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#aaa", fontSize: 14 }}>
              No compliance templates found.
            </div>
          )}
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              style={{
                border: "1px solid #e8e8e8",
                borderRadius: 8,
                padding: "16px 20px",
                background: tpl.isActive ? "#fff" : "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <FileProtectOutlined style={{ fontSize: 24, color: "#014c75" }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{tpl.title}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    <Tag style={{ marginRight: 4 }}>{tpl.id}</Tag>
                    <Tag color={tpl.docType === "nda" ? "blue" : tpl.docType === "contract" ? "purple" : "cyan"}>
                      {DOC_TYPE_LABELS_MAP[tpl.docType] || tpl.docType}
                    </Tag>
                    {tpl.isActive ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>
                    ) : (
                      <Tag color="default">Inactive — will not be generated for new hirings</Tag>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
                    Last updated by: {tpl.updatedBy || "—"} ·{" "}
                    {tpl.updatedAt ? new Date(tpl.updatedAt).toLocaleDateString("en-GB") : "—"}
                  </div>
                </div>
              </div>
              <Button icon={<EditOutlined />} onClick={() => openEdit(tpl)}>Edit Template</Button>
            </div>
          ))}
        </div>
      </Spin>

      <Drawer
        open={editDrawerOpen}
        onClose={() => { setEditDrawerOpen(false); setEditingTemplate(null); editForm.resetFields(); }}
        title={
          <span>
            <FileProtectOutlined style={{ marginRight: 8 }} />
            Edit: {editingTemplate?.title}
          </span>
        }
        width={840}
        extra={
          <Space>
            <Button onClick={handlePreview} loading={previewingTemplate}>Preview with Sample Data</Button>
            <Button type="primary" loading={savingTemplate} onClick={handleSave} style={{ background: "#00d9a9", borderColor: "#00d9a9" }}>
              Save Template
            </Button>
          </Space>
        }
      >
        {editingTemplate && (
          <Form form={editForm} layout="vertical">
            <Form.Item name="title" label="Document Title" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item
              label="Template Content"
              help={
                <span>
                  Use placeholders: <code>{"{{TALENT_NAME}}"}</code>, <code>{"{{AADHAAR_NUMBER}}"}</code>,{" "}
                  <code>{"{{JOB_TITLE}}"}</code>, <code>{"{{CUSTOMER_ORG}}"}</code>,{" "}
                  <code>{"{{START_DATE}}"}</code>, <code>{"{{CONTRACT_DURATION}}"}</code>,{" "}
                  <code>{"{{SIGNED_DATE}}"}</code>, <code>{"{{SIGNED_LOCATION}}"}</code>
                </span>
              }
              style={{ marginBottom: 8 }}
            >
              <ReactQuill
                theme="snow"
                value={editorContent}
                onChange={setEditorContent}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                style={{ height: 420, marginBottom: 42, fontFamily: "Georgia, serif" }}
              />
            </Form.Item>
            <Form.Item name="isActive" label="Status" valuePropName="checked">
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Form.Item>
          </Form>
        )}
      </Drawer>

      <Modal
        open={previewModal.open}
        onCancel={() => setPreviewModal({ open: false, content: "" })}
        title="Template Preview (Sample Data)"
        width={900}
        footer={<Button onClick={() => setPreviewModal({ open: false, content: "" })}>Close</Button>}
        bodyStyle={{ maxHeight: "72vh", overflowY: "auto", padding: 0 }}
      >
        <ComplianceDocViewer
          document={{
            status: "signed",
            docType: editingTemplate?.docType || "nda",
            templateTitle: editingTemplate?.title || "",
            renderedContent: previewModal.content,
            signedAt: new Date().toISOString(),
          }}
          open={previewModal.open}
          onClose={() => setPreviewModal({ open: false, content: "" })}
          mode="inline"
        />
      </Modal>
    </ContentSection>
  );
};

export default Settings;
